/**
 * Client RA Bills — receivables (Ellora billing its clients).
 * Prompt-1 Part C. Tab inside the Finance module.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, X, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';

const STATUS_BADGE = {
  Draft:      'bg-slate-100 text-slate-600 border-slate-200',
  Submitted:  'bg-blue-50 text-blue-700 border-blue-200',
  Certified:  'bg-violet-50 text-violet-700 border-violet-200',
  Paid:       'bg-emerald-100 text-emerald-800 border-emerald-200',
  Approved:   'bg-emerald-50 text-emerald-700 border-emerald-200',   // legacy alias
  Collected:  'bg-emerald-100 text-emerald-800 border-emerald-200',  // legacy alias
};

/** Standalone page at /finance/client-bills (sidebar entry). */
export function ClientBillsPage() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="kpi-overline">Finance &amp; Accounts</div>
        <h1 className="font-display font-bold text-2xl mt-1">Client RA Bills</h1>
        <p className="text-sm text-slate-600 mt-1">Receivables — Ellora billing its clients. Draft → Submitted → Approved → Collected.</p>
      </div>
      <ClientBillsPanel projects={projects} />
    </div>
  );
}

export default function ClientBillsPanel({ projects }) {
  const [projectId, setProjectId] = useState('');
  const [bills, setBills] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [certifying, setCertifying] = useState(null);   // bill being certified
  const [certForm, setCertForm] = useState({ certified_amount: '', cert_date: '', cert_ref: '' });
  const [paying, setPaying] = useState(null);            // bill being marked paid

  const load = () => apiGet('/client-bills', projectId ? { project_id: projectId } : {})
    .then((d) => setBills(d.items || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const submit = async (b) => {
    setBusyId(b.id);
    try {
      await apiPatch(`/client-bills/${b.id}/submit`);
      toast.success(`${b.ra_bill_no} submitted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };

  const certify = async () => {
    if (!certifying) return;
    try {
      await apiPatch(`/client-bills/${certifying.id}/certify`, {
        certified_amount: parseFloat(certForm.certified_amount),
        cert_date: certForm.cert_date,
        cert_ref: certForm.cert_ref,
      });
      toast.success(`${certifying.ra_bill_no} certified`);
      setCertifying(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const markPaid = async (b) => {
    setPaying(b.id);
    try {
      await apiPatch(`/client-bills/${b.id}/mark-paid`, { paid_date: new Date().toISOString().slice(0, 10) });
      toast.success(`${b.ra_bill_no} marked as paid`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setPaying(null); }
  };

  return (
    <div className="space-y-4" data-testid="client-bills-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[240px]" data-testid="cb-project-select">
          <option value="">All projects</option>
          {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={() => setCreating(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm font-display font-bold"
          data-testid="cb-new-bill-btn">
          <Plus size={13} /> New Bill
        </button>
      </div>

      <table className="w-full dense-table text-xs">
        <thead><tr><th>Bill No</th><th>Project</th><th>Client</th><th>Date</th>
          <th className="text-right">Gross</th><th className="text-right">Certified</th>
          <th className="text-right">Net Receivable</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {bills.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No client RA bills yet</td></tr>}
          {bills.map((b) => (
            <tr key={b.id} data-testid={`cb-row-${b.ra_bill_no}`}>
              <td className="font-mono">{b.ra_bill_no}</td>
              <td className="truncate max-w-[140px]">{b.project_name}</td>
              <td className="truncate max-w-[120px]">{b.client_name}</td>
              <td>{formatDate(b.bill_date)}</td>
              <td className="text-right tabular-nums">{formatINR(b.gross_amount)}</td>
              <td className="text-right tabular-nums">
                {b.certified_amount != null
                  ? <span className="font-bold">{formatINR(b.certified_amount)}
                      {b.certification_shortfall > 0 && <span className="text-rose-600 ml-1">(−{formatINR(b.certification_shortfall)})</span>}
                    </span>
                  : <span className="text-slate-300">—</span>}
              </td>
              <td className="text-right tabular-nums font-display font-bold">{formatINR(b.net_payable)}</td>
              <td><span className={`px-1.5 py-0.5 text-[10px] border rounded-sm font-display font-bold uppercase ${STATUS_BADGE[b.status] || STATUS_BADGE.Draft}`}>{b.status}</span></td>
              <td className="flex items-center gap-1">
                {b.status === 'Draft' && (
                  <button onClick={() => submit(b)} disabled={busyId === b.id}
                    className="px-2 py-1 text-[10px] bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50"
                    data-testid={`cb-submit-${b.ra_bill_no}`}>
                    {busyId === b.id ? '…' : 'Submit'}
                  </button>
                )}
                {b.status === 'Submitted' && (
                  <button onClick={() => { setCertifying(b); setCertForm({ certified_amount: b.gross_amount, cert_date: new Date().toISOString().slice(0, 10), cert_ref: '' }); }}
                    className="px-2 py-1 text-[10px] bg-violet-600 text-white rounded-sm font-display font-bold"
                    data-testid={`cb-certify-${b.ra_bill_no}`}>
                    Certify
                  </button>
                )}
                {b.status === 'Certified' && (
                  <button onClick={() => markPaid(b)} disabled={paying === b.id}
                    className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50"
                    data-testid={`cb-paid-${b.ra_bill_no}`}>
                    {paying === b.id ? '…' : 'Mark Paid'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Certification drawer */}
      {certifying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="certify-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-base">Certify Bill — {certifying.ra_bill_no}</div>
                <div className="text-xs text-slate-500 mt-0.5">Record PMC / consultant certification</div>
              </div>
              <button onClick={() => setCertifying(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3">
              Submitted amount: <strong>{formatINR(certifying.gross_amount)}</strong>
              {parseFloat(certForm.certified_amount) < certifying.gross_amount && certForm.certified_amount && (
                <span className="ml-2 text-rose-600">Shortfall: {formatINR(certifying.gross_amount - parseFloat(certForm.certified_amount))}</span>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Certified Amount *</label>
                <input type="number" value={certForm.certified_amount}
                  onChange={(e) => setCertForm(f => ({ ...f, certified_amount: e.target.value }))}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                  data-testid="certify-amount" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Cert Date *</label>
                  <input type="date" value={certForm.cert_date}
                    onChange={(e) => setCertForm(f => ({ ...f, cert_date: e.target.value }))}
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                    data-testid="certify-date" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">PMC Cert Ref</label>
                  <input type="text" value={certForm.cert_ref}
                    onChange={(e) => setCertForm(f => ({ ...f, cert_ref: e.target.value }))}
                    placeholder="PMC/2026/001"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                    data-testid="certify-ref" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={certify}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white py-2 rounded font-display font-bold text-sm"
                data-testid="certify-confirm-btn">
                Confirm Certification
              </button>
              <button onClick={() => setCertifying(null)} className="px-4 py-2 text-sm border border-slate-300 rounded text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <NewBillDrawer projects={projects} defaultProjectId={projectId}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function NewBillDrawer({ projects, defaultProjectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    project_id: defaultProjectId || '',
    client_name: '', client_gstin: '',
    bill_date: new Date().toISOString().slice(0, 10),
    due_date: '', gross_amount: '', gst_pct: 18, retention_pct: 0, tds_pct: 1,
    sac_code: '9954', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pre-fill client name from the selected project.
  useEffect(() => {
    const p = (projects || []).find((x) => x.id === form.project_id);
    if (p && !form.client_name) set('client_name', p.client || p.client_name || '');
    // eslint-disable-next-line
  }, [form.project_id]);

  const totals = useMemo(() => {
    const gross = Number(form.gross_amount) || 0;
    const gst = gross * (Number(form.gst_pct) || 0) / 100;
    const retention = gross * (Number(form.retention_pct) || 0) / 100;
    const tds = gross * (Number(form.tds_pct) || 0) / 100;
    return { gross, gst, retention, tds, net: gross + gst - retention - tds };
  }, [form.gross_amount, form.gst_pct, form.retention_pct, form.tds_pct]);

  const save = async () => {
    const proj = (projects || []).find((p) => p.id === form.project_id);
    if (!proj) return toast.error('Select a project');
    if (!form.client_name) return toast.error('Client name is required');
    if (!(Number(form.gross_amount) > 0)) return toast.error('Gross amount must be > 0');
    setSaving(true);
    try {
      const res = await apiPost('/client-bills', {
        ...form,
        project_name: proj.name,
        gross_amount: Number(form.gross_amount),
        gst_pct: Number(form.gst_pct), retention_pct: Number(form.retention_pct), tds_pct: Number(form.tds_pct),
        due_date: form.due_date || null,
      });
      toast.success(`${res.bill.ra_bill_no} created (Draft)`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create bill'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" data-testid="cb-new-drawer">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="kpi-overline flex items-center gap-1.5"><Receipt size={11} /> Client RA Bill</div>
            <h3 className="font-display font-bold text-lg">New Bill</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Project *">
            <select value={form.project_id} onChange={(e) => set('project_id', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="cb-form-project">
              <option value="">Select project</option>
              {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Client Name *">
            <input value={form.client_name} onChange={(e) => set('client_name', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="cb-form-client" />
          </Field>
          <Field label="Client GSTIN">
            <input value={form.client_gstin} onChange={(e) => set('client_gstin', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Bill Date *">
              <input type="date" value={form.bill_date} onChange={(e) => set('bill_date', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="cb-form-date" />
            </Field>
            <Field label="Due Date">
              <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
          </div>
          <Field label="Gross Amount (₹) *">
            <input type="number" value={form.gross_amount} onChange={(e) => set('gross_amount', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="cb-form-gross" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="GST %"><input type="number" value={form.gst_pct} onChange={(e) => set('gst_pct', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
            <Field label="Retention %"><input type="number" value={form.retention_pct} onChange={(e) => set('retention_pct', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
            <Field label="TDS %"><input type="number" value={form.tds_pct} onChange={(e) => set('tds_pct', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></Field>
          </div>
          <Field label="SAC Code">
            <input value={form.sac_code} onChange={(e) => set('sac_code', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </Field>
          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </Field>

          {/* Live total */}
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1 text-xs" data-testid="cb-live-totals">
            <Row label="Gross" v={totals.gross} />
            <Row label={`+ GST (${form.gst_pct}%)`} v={totals.gst} />
            <Row label={`− Retention (${form.retention_pct}%)`} v={-totals.retention} />
            <Row label={`− TDS (${form.tds_pct}%)`} v={-totals.tds} />
            <div className="border-t border-slate-300 pt-1"><Row label="= Net Receivable" v={totals.net} bold /></div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm font-display font-bold">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-1.5 text-xs bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm font-display font-bold disabled:opacity-50 flex items-center gap-1.5"
            data-testid="cb-form-save">
            {saving && <Loader2 size={12} className="animate-spin" />} Create Bill
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-display font-bold mb-1">{label}</label>
    {children}
  </div>
);

const Row = ({ label, v, bold }) => (
  <div className={`flex justify-between ${bold ? 'font-display font-bold' : 'text-slate-600'}`}>
    <span>{label}</span>
    <span className="tabular-nums">{formatINR(Math.abs(v))}{v < 0 ? ' (−)' : ''}</span>
  </div>
);
